import React, { useState } from 'react'
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { YStack, XStack, useTheme } from 'tamagui'

import { EventCard } from '@newComponents/Event/EventCard'
import { PresentationCard } from '@newComponents/Presentation/PresentationCard'
import { UserCard } from '@newComponents/User/UserCard'
import { Paragraph, Text } from '@newComponents/Text'
import { Icon } from '@newComponents/Icon'
import { Card } from '@newComponents/Card'
import { FormattedMarkdown } from '@newComponents/FormattedMarkdown/FormattedMarkdown'
import { Tooltip } from '@newComponents/Tooltip'
import { CalendarSubscribeDialog } from '@newComponents/CalendarSubscribeDialog/CalendarSubscribeDialog'
import { GuildSocialProfileLinks } from '@newComponents/SocialProfileLinks/GuildSocialProfileLinks'
import { ExploreMoreLink } from '../HomeScreen/ExploreMoreLink'

import type { GuildBentoScreenQuery } from './__generated__/GuildBentoScreenQuery.graphql'
import type { GuildBentoScreenMember_guildMembership$key } from './__generated__/GuildBentoScreenMember_guildMembership.graphql'

const SectionHeader = ({
  title,
  href,
  extra,
}: {
  title: string
  href?: string
  extra?: React.ReactNode
}) => (
  <XStack justifyContent='space-between' alignItems='center'>
    <Text fontWeight='$7' size='$7'>
      {title}
    </Text>
    <XStack alignItems='center' gap='$3'>
      {extra}
      {href && <ExploreMoreLink href={href}>More</ExploreMoreLink>}
    </XStack>
  </XStack>
)

const MemberHighlight = ({
  membership,
}: {
  membership: GuildBentoScreenMember_guildMembership$key
}) => {
  const data = useFragment(
    graphql`
      fragment GuildBentoScreenMember_guildMembership on GuildMembership {
        id
        membershipLevel
        user {
          ...UserCardNew_user

          recentPresentations: presentationsByPresenterId(
            first: 3
            orderBy: [CREATED_AT_DESC]
          ) {
            totalCount
            edges {
              node {
                title
              }
            }
          }

          totalEventAttendance: eventAttendance {
            totalCount
          }
        }
      }
    `,
    membership
  )

  if (!data.user) return null

  const highlights: string[] = []

  const presentationTitles = data.user.recentPresentations.edges
    .map((e) => e.node.title)
    .filter(Boolean)

  if (presentationTitles.length > 0) {
    const totalCount = data.user.recentPresentations.totalCount
    if (totalCount === 1) {
      highlights.push(`Gave a talk on "${presentationTitles[0]}"`)
    } else if (totalCount <= 3) {
      highlights.push(`Spoke about ${presentationTitles.join(' and ')}`)
    } else {
      highlights.push(
        `${totalCount} talks including "${presentationTitles[0]}"`
      )
    }
  }

  const eventCount = data.user.totalEventAttendance.totalCount
  if (eventCount > 0) {
    highlights.push(
      `Attended ${eventCount} Event${eventCount === 1 ? '' : 's'}`
    )
  }

  return (
    <UserCard user={data.user} limitDescriptionLines={2} expand>
      {highlights.length > 0 && (
        <YStack gap='$2'>
          {highlights.map((highlight, i) => (
            <XStack key={i} gap='$2' alignItems='center'>
              <Icon
                name={
                  i === 0 && presentationTitles.length > 0
                    ? 'projection-screen'
                    : 'calendar'
                }
                size='$4'
                color='$colorMuted'
              />
              <Text size='$4' color='$color' numberOfLines={1} flex={1}>
                {highlight}
              </Text>
            </XStack>
          ))}
        </YStack>
      )}
    </UserCard>
  )
}

const MAX_HIGHLIGHTED_MEMBERS = 4

const CalendarButton = ({ onPress }: { onPress: () => void }) => {
  const theme = useTheme()

  return (
    <Tooltip placement='top' delay={100}>
      <Tooltip.Trigger tag='dialog'>
        <XStack
          cursor='pointer'
          onPress={onPress}
          padding='$2'
          animation='medium'
          hoverStyle={{ opacity: 0.7 }}
        >
          <Icon
            name='calendar-add'
            color='$colorMuted'
            secondaryColor={theme.primaryColor.val}
            size='$6'
          />
        </XStack>
      </Tooltip.Trigger>

      <Tooltip.Content>
        <Tooltip.Arrow />
        <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
          Add to Calendar
        </Paragraph>
      </Tooltip.Content>
    </Tooltip>
  )
}

export const GuildBentoScreen = () => {
  const params = useParams<'actorId'>()

  const data = useLazyLoadQuery<GuildBentoScreenQuery>(
    graphql`
      query GuildBentoScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            id
            slugId
            name
            description
            memberCount

            ...GuildSocialProfileLinks_guild

            myMembership {
              membershipLevel
            }

            upcomingEvents: eventsByTimeSelection(
              first: 4
              timeSelection: UPCOMING
              orderBy: [START_AT_ASC]
            ) {
              edges {
                node {
                  id
                  ...EventCardNew_event @arguments(includeAttendRow: true)
                }
              }
            }

            recentPresentations: presentations(
              first: 4
              orderBy: [CREATED_AT_DESC]
            ) {
              edges {
                node {
                  id
                  ...PresentationCardNew_presentation
                }
              }
            }

            membersWithPhotos: guildMemberships(
              first: 4
              orderBy: [MEMBERSHIP_LEVEL_ASC]
              filter: { userHasPhoto: { equalTo: true } }
            ) {
              edges {
                node {
                  ...GuildBentoScreenMember_guildMembership
                }
              }
            }

            membersWithoutPhotos: guildMemberships(
              first: 4
              orderBy: [MEMBERSHIP_LEVEL_ASC]
              filter: { userHasPhoto: { equalTo: false } }
            ) {
              edges {
                node {
                  ...GuildBentoScreenMember_guildMembership
                }
              }
            }

            pastEvents: eventsByTimeSelection(
              first: 3
              timeSelection: PAST
              orderBy: [START_AT_DESC]
            ) {
              edges {
                node {
                  id
                  ...EventCardNew_event
                }
              }
            }
          }
        }

        ...EventCardNew_query
      }
    `,
    { id: params.actorId || '' }
  )

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  const guild = data.nodeBySlugId
  const upcomingEvents = guild.upcomingEvents.edges
  const presentations = guild.recentPresentations.edges
  const pastEvents = guild.pastEvents.edges
  const isOrganizer = guild.myMembership?.membershipLevel === 'ORGANIZER'
  const isEditor = guild.myMembership?.membershipLevel === 'EDITOR'
  const canEdit = isOrganizer || isEditor

  const navigate = useNavigate()
  const location = useLocation()
  const [showCalendarDialog, setShowCalendarDialog] = useState(false)

  const hasUpcoming = upcomingEvents.length > 0
  const hasPresentations = presentations.length > 0
  const hasPastEvents = pastEvents.length > 0

  // Server-side photo prioritization: members with photos first, then fill remaining slots
  const memberEdges = [
    ...guild.membersWithPhotos.edges,
    ...guild.membersWithoutPhotos.edges,
  ].slice(0, MAX_HIGHLIGHTED_MEMBERS)
  const hasMembers = memberEdges.length > 0

  return (
    <YStack gap='$8' paddingBottom={80} $gtSm={{ paddingBottom: 120 }}>
      {/* Upcoming Events */}
      {hasUpcoming && (
        <YStack gap='$5'>
          <SectionHeader
            title='Upcoming Events'
            href={`/${guild.slugId}/events`}
            extra={
              <CalendarButton onPress={() => setShowCalendarDialog(true)} />
            }
          />

          <XStack gap='$4' flexWrap='wrap'>
            {upcomingEvents.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis='45%' minWidth={240}>
                <EventCard
                  event={node}
                  query={data}
                  showOrganizer={false}
                  showAttendRow
                />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}

      {/* Organizer CTAs: Create event + Add presentation */}
      {canEdit && ((!hasUpcoming && !hasPastEvents) || !hasPresentations) && (
        <XStack gap='$4' flexWrap='wrap'>
          {!hasUpcoming && !hasPastEvents && (
            <YStack flex={1} flexBasis='45%' minWidth={240}>
              <Card
                glass
                padding='$7'
                borderWidth={1}
                borderColor='$borderColor'
                hoverStyle={{ scale: 1.01 }}
                pressStyle={{ scale: 0.99 }}
                animation='fast'
                cursor='pointer'
                height='100%'
                onPress={() =>
                  navigate('/events/new', {
                    state: { backgroundLocation: location },
                  })
                }
              >
                <XStack gap='$5' alignItems='center' flex={1}>
                  <YStack
                    width={48}
                    height={48}
                    borderRadius='$4'
                    backgroundColor='$backgroundAccent'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Icon name='calendar' size='$6' color='$colorStrong' />
                  </YStack>
                  <YStack gap='$1' flex={1}>
                    <Text fontWeight='$7' size='$6'>
                      Create your first Event
                    </Text>
                    <Paragraph color='$colorSubtle'>
                      Host a meetup, workshop, or talk for your community
                    </Paragraph>
                  </YStack>
                  <Icon name='arrow-right' size='$5' color='$colorMuted' />
                </XStack>
              </Card>
            </YStack>
          )}

          {!hasPresentations && (
            <YStack flex={1} flexBasis='45%' minWidth={240}>
              <Card
                glass
                padding='$7'
                borderWidth={1}
                borderColor='$borderColor'
                hoverStyle={{ scale: 1.01 }}
                pressStyle={{ scale: 0.99 }}
                animation='fast'
                cursor='pointer'
                height='100%'
                onPress={() =>
                  navigate('/presentations/new', {
                    state: { backgroundLocation: location },
                  })
                }
              >
                <XStack gap='$5' alignItems='center' flex={1}>
                  <YStack
                    width={48}
                    height={48}
                    borderRadius='$4'
                    backgroundColor='$backgroundAccent'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Icon
                      name='projection-screen'
                      size='$6'
                      color='$colorStrong'
                    />
                  </YStack>
                  <YStack gap='$1' flex={1}>
                    <Text fontWeight='$7' size='$6'>
                      Add a Presentation
                    </Text>
                    <Paragraph color='$colorSubtle'>
                      Share talks, slides, or recordings
                    </Paragraph>
                  </YStack>
                  <Icon name='arrow-right' size='$5' color='$colorMuted' />
                </XStack>
              </Card>
            </YStack>
          )}
        </XStack>
      )}

      {/* Presentations */}
      {hasPresentations && (
        <YStack gap='$5'>
          <SectionHeader
            title='Presentations'
            href={`/${guild.slugId}/presentations`}
          />

          <XStack gap='$4' flexWrap='wrap'>
            {presentations.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis='45%' minWidth={240}>
                <PresentationCard presentation={node} />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}

      {/* Members */}
      {hasMembers && (
        <YStack gap='$5'>
          <SectionHeader title='Members' href={`/${guild.slugId}/members`} />

          <XStack gap='$4' flexWrap='wrap'>
            {memberEdges.map(({ node }, index) => {
              if (!node) return null
              return (
                <YStack key={index} flex={1} flexBasis='45%' minWidth={240}>
                  <MemberHighlight membership={node} />
                </YStack>
              )
            })}
          </XStack>
        </YStack>
      )}

      {/* About */}
      {guild.description && (
        <YStack gap='$4'>
          <SectionHeader title='About' />
          <Card glass padding='$5'>
            <YStack gap='$4'>
              <FormattedMarkdown>{guild.description}</FormattedMarkdown>
              <GuildSocialProfileLinks guild={guild} size='$5' />
            </YStack>
          </Card>
        </YStack>
      )}

      {/* Past events — shown only when no upcoming events */}
      {!hasUpcoming && hasPastEvents && (
        <YStack gap='$5'>
          <SectionHeader
            title='Recent Events'
            href={`/${guild.slugId}/events`}
          />

          <XStack gap='$4' flexWrap='wrap'>
            {pastEvents.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis='30%' minWidth={200}>
                <EventCard event={node} />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}

      <CalendarSubscribeDialog
        open={showCalendarDialog}
        onOpenChange={setShowCalendarDialog}
        slug={guild.slugId}
      />
    </YStack>
  )
}

export default GuildBentoScreen
